import React, { useState, useRef, useEffect } from 'react';

function CustomSelect({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Select...', 
  disabled = false,
  required = false,
  className = '',
  name = '',
  searchable = true,
  size = 'default' // 'default' (text-sm) or 'sm' (text-xs)
}) {
  const textSizeClass = size === 'sm' ? 'text-xs' : 'text-sm';
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find(opt => {
    const optValue = typeof opt === 'object' ? opt.value : opt;
    // Handle both string and numeric comparisons
    return String(optValue) === String(value) || optValue === value;
  });

  const displayValue = selectedOption 
    ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption)
    : placeholder;

  // Filter options based on search term
  const filteredOptions = searchable && searchTerm
    ? options.filter(option => {
        const optionLabel = typeof option === 'object' ? option.label : option;
        const optionValue = typeof option === 'object' ? option.value : option;
        const searchLower = searchTerm.toLowerCase();
        return (
          String(optionLabel).toLowerCase().includes(searchLower) ||
          String(optionValue).toLowerCase().includes(searchLower)
        );
      })
    : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(-1);
    }
  }, [filteredOptions.length, highlightedIndex]);

  const handleSelect = (option) => {
    const optionValue = typeof option === 'object' ? option.value : option;
    onChange({
      target: {
        name: name,
        value: optionValue
      }
    });
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    // If typing alphanumeric characters when dropdown is closed, open it and start searching
    if (!isOpen && /^[a-zA-Z0-9]$/.test(e.key)) {
      setIsOpen(true);
      setSearchTerm(e.key);
      return;
    }

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(!isOpen);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(-1); // Reset highlight when search changes
  };

  return (
    <div className="relative">
      <button
        type="button"
        ref={selectRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-2.5 py-1.5 ${textSizeClass} text-left bg-white border border-slate-300 rounded-md
          focus:outline-none focus:ring-1 focus:ring-eucnet-sky focus:border-eucnet-sky
          transition-all duration-150
          ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-eucnet-sky'}
          ${isOpen ? 'border-eucnet-sky ring-1 ring-eucnet-sky' : ''}
          ${className}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center justify-between">
          <span className={`${textSizeClass} ${value ? 'text-slate-900' : 'text-slate-500'} ${size === 'sm' ? 'whitespace-nowrap' : ''}`}>
            {displayValue}
          </span>
          <svg
            className={`w-4 h-4 text-eucnet-blue transition-transform duration-150 flex-shrink-0 ml-2 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-0.5 bg-white border border-eucnet-sky rounded-md shadow-xl"
          role="listbox"
        >
          {searchable && (
            <div className="p-2 border-b border-slate-200">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  // Prevent Enter from submitting forms when typing in search
                  if (e.key === 'Enter' && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    e.preventDefault();
                    handleSelect(filteredOptions[highlightedIndex]);
                  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (e.key === 'ArrowDown') {
                      setHighlightedIndex(prev => 
                        prev < filteredOptions.length - 1 ? prev + 1 : prev
                      );
                    } else {
                      setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                    }
                  }
                }}
                placeholder="Type to search..."
                className={`w-full px-2.5 py-1.5 ${textSizeClass} border border-slate-300 rounded-md
                  focus:outline-none focus:ring-1 focus:ring-eucnet-sky focus:border-eucnet-sky`}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="max-h-56 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-2.5 py-2 text-xs text-slate-500 text-center">
                {searchTerm ? 'No options match your search' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : option;
                // Handle both string and numeric comparisons
                const isSelected = String(optionValue) === String(value) || optionValue === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    key={optionValue}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`
                      px-2.5 py-1.5 ${textSizeClass} cursor-pointer transition-colors duration-100
                      ${isSelected 
                        ? 'bg-eucnet-blue text-white font-medium' 
                        : isHighlighted
                        ? 'bg-eucnet-light text-eucnet-blue'
                        : 'text-slate-700 hover:bg-eucnet-light hover:text-eucnet-blue'
                      }
                    `}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {optionLabel}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Hidden input for form submission */}
      {required && (
        <input
          type="hidden"
          name={name}
          value={value || ''}
          required={required}
        />
      )}
    </div>
  );
}

export default CustomSelect;
